<script setup lang="ts">
  import {
    DROPDOWN_CONTEXT_KEY,
    DropdownApplyFooter,
    DropdownPanelScroll,
    LoaderContainer,
    Radio,
    RadioGroup,
    RadioGroupOrientation,
    SearchInput,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { vInfiniteScroll } from '@vueuse/components';
  import { computed, inject, onMounted, ref, watch } from 'vue';
  import { useFilters } from '../../../../composables';
  import type { SingleSelectModelValue } from '../../../../store';
  import { FilterTypes, getSelectOptionKey } from '../../../../utils';
  import {
    loaderContainerClass,
    noResultsClass,
    optionRowClass,
    panelScrollClass,
    searchClass,
    selectPaginatedFilterClass,
  } from './SelectPaginatedFilter.css.ts';

  const { t } = useLocale('table');

  const props = withDefaults(
    defineProps<{
      filterNamespace: string;
      labelKey?: string;
      valueKey?: string;
      items: unknown[];
      canLoadMore: () => boolean;
      onSearch: (value: string) => void;
      onLoadMore: () => void;
    }>(),
    {
      labelKey: 'name',
      valueKey: 'id',
    },
  );

  defineOptions({
    inheritAttrs: false,
  });

  const dropdown = inject(DROPDOWN_CONTEXT_KEY, null);

  const currentFilterStore = useFilters(props.filterNamespace);

  const itemIsChanging = ref(false);

  const search = ref('');

  const options = computed(() =>
    props.items.map((item) => {
      const castedItem = item as Record<string, unknown>;

      return {
        label: `${castedItem[props.labelKey]}`,
        value: `${castedItem[props.valueKey]}`,
      };
    }),
  );

  const innerCanLoadMore = computed(() => {
    return () => !itemIsChanging.value && props.canLoadMore();
  });

  const storeProps = computed(() =>
    currentFilterStore.getFilters.value?.find(
      (f) => f.filterType === FilterTypes.SELECT_PAGINATED,
    ),
  );

  const selectedItem = ref<SingleSelectModelValue | undefined>(
    storeProps.value?.modelValue as SingleSelectModelValue | undefined,
  );

  const selectedOption = ref<string | undefined>(
    getSelectOptionKey(
      (storeProps.value?.modelValue as SingleSelectModelValue | undefined)
        ?.value,
    ),
  );

  watch(
    () => storeProps.value?.modelValue,
    (value) => {
      const selected = value as SingleSelectModelValue | undefined;
      selectedItem.value = selected;
      selectedOption.value = getSelectOptionKey(selected?.value);
    },
  );

  watch(
    () => props.items,
    () => {
      itemIsChanging.value = false;
    },
  );

  const internalOnLoadMore = async () => {
    itemIsChanging.value = true;
    props.onLoadMore();
  };

  const onApplyFilter = () => {
    currentFilterStore.applyFilterValue({
      filterFieldName: storeProps.value!.filterFieldName,
      value: selectedItem.value,
      filterType: FilterTypes.SELECT_PAGINATED,
    });
    dropdown?.close();
  };

  const onSelectItem = (item: SingleSelectModelValue) => {
    selectedItem.value = item;
    selectedOption.value = getSelectOptionKey(item.value);
  };

  const onSelectByValue = (value: string) => {
    const option = options.value.find(
      (o) => getSelectOptionKey(o.value) === value,
    );
    if (option) onSelectItem(option);
  };

  onMounted(() => {
    if (storeProps.value?.modelValue) {
      onSelectItem(storeProps.value.modelValue as SingleSelectModelValue);
    }
  });

  const onUpdateModelValue = (value: string) => {
    props.onSearch(value);
  };
</script>

<template>
  <div :class="selectPaginatedFilterClass">
    <SearchInput
      :class="searchClass"
      name="search-select"
      :placeholder="t('filters.select_paginated.placeholder')"
      v-model="search"
      @update:model-value="onUpdateModelValue"
      :clearable="true"
    />

    <DropdownPanelScroll
      :class="panelScrollClass"
      v-infinite-scroll="[
        internalOnLoadMore,
        { canLoadMore: innerCanLoadMore },
      ]"
    >
      <RadioGroup
        :model-value="selectedOption ?? ''"
        name="select-paginated"
        :orientation="RadioGroupOrientation.vertical"
        :with-gap="false"
        @update:model-value="onSelectByValue"
      >
        <Radio
          v-for="option in options"
          :key="option.value"
          :class="optionRowClass"
          :value="option.value"
          :label="option.label"
        />
      </RadioGroup>
      <LoaderContainer :class="loaderContainerClass" v-if="itemIsChanging" />
      <div
        :class="noResultsClass"
        v-if="options.length === 0 && !itemIsChanging"
      >
        {{ t('filters.select_paginated.no_result') }}
      </div>
    </DropdownPanelScroll>
  </div>

  <DropdownApplyFooter :disabled="!selectedItem" @apply="onApplyFilter" />
</template>
